import logo from '../images/logo/logo.png'
import { v4 as uuidv4 } from 'uuid'
import { toaster } from '@/components/lib/Toast'
import api from '@/api'

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
export const ALLOWED_MESSAGE_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.docx'
export const TEAM_PERMISSIONS = [
  { key: 'users.create', description: 'Create and invite users' },
  { key: 'users.delete', description: 'Delete users' },
  { key: 'users.manage', description: 'Update users and manage permissions' },
  { key: 'trials.create', description: 'Create trials' },
  { key: 'trials.manage', description: 'Manage any trial' },
  { key: 'team.access', description: 'Access the team account pages' },
  { key: 'team.manage', description: 'Manage the team account' }
]
export const TRIAL_PERMISSIONS = [
  // We don't include trial.access as it's more of a system permission
  { key: 'trial.admin', description: 'Trial administrator (full access) (no-PII)' },
  { key: 'trial.read', description: 'Trial-wide admin view access (no-PII)' },
  { key: 'trial.write', description: 'Trial-wide admin write access' },
  { key: 'trial.readParticipants', description: 'List all participants in the trial' },
  { key: 'trial.writeParticipants', description: 'Update participant details' },
  { key: 'trial.readParticipantPersonalInformation', description: 'View participant personal profile and contact details (PII)' },
  { key: 'trial.readParticipantConsent', description: 'View a participant\'s consent information (PII)' },
  { key: 'trial.readTrialConsents', description: 'List all trial consents (PII)' },
  { key: 'trial.readParticipantNotes', description: 'View participant notes and communication logs' },
  { key: 'trial.writeParticipantNotes', description: 'Add notes to participants' },
  { key: 'trial.readParticipantStudyData', description: 'View participant study data (excluding restricted and PII fields)' },
  { key: 'trial.readParticipantStudyDataRestricted', description: 'View restricted participant study data fields (restricted)' },
  { key: 'trial.readParticipantStudyDataPii', description: 'View PII participant study data fields (PII)' },
  { key: 'trial.writeParticipantStudyData', description: 'Create & update participant study data' },
  { key: 'trial.readFiles', description: 'View all files in the eTMF' },
  { key: 'trial.readApprovedFiles', description: 'View approved files only in the eTMF' },
  { key: 'trial.readFileVersions', description: 'View file versions in the eTMF' },
  { key: 'trial.writeFiles', description: 'Create new files and versions in the eTMF' },
  { key: 'trial.deleteFiles', description: 'Delete all files and versions in the eTMF' },
  { key: 'trial.requestFileApproval', description: 'Request eTMF file approval' },
  { key: 'trial.approveFiles', description: 'Approve all eTMF files' },
  { key: 'trial.readUsers', description: 'View trial users' },
  { key: 'trial.support', description: 'Send & receive support messages' },
  { key: 'trial.readTasks', description: 'List all trial tasks' },
  { key: 'trial.writeTasks', description: 'Create and manage trial tasks' },
  { key: 'trial.readQueries', description: 'List all trial queries' },
  { key: 'trial.writeQueries', description: 'Raise trial queries' },
  { key: 'trial.readAutomaticQueries', description: 'View automatic trial queries' },
  { key: 'trial.writeAutomaticQueries', description: 'Create automatic trial queries' },
  { key: 'trial.readSites', description: 'View trial sites' },
  { key: 'trial.writeSites', description: 'Create & update trial sites' },
  { key: 'trial.listPacks', description: 'List & view packs' },
  { key: 'trial.writePacks', description: 'Create & update packs' },
  { key: 'trial.deletePacks', description: 'Delete packs' },
  { key: 'trial.assignPacks', description: 'List and assign available packs to participants' },
  { key: 'trial.createParticipantMeetings', description: 'Create online video call meetings with participants' },
  { key: 'trial.readParticipantMeetings', description: 'View online video call meetings with participants' },
  { key: 'trial.joinParticipantMeetings', description: 'Join participant video call meetings' },
  { key: 'trial.deleteParticipantMeetings', description: 'Delete online video call meetings with participants' },
  { key: 'trial.manageWallet', description: 'Manage (view and top-up) the trial wallet' },
  { key: 'trial.useAssistant', description: 'Use the trial assistant' },
  { key: 'trial.readInsights', description: 'View trial and participant insights' },
  { key: 'trial.readMessages', description: 'Read all site message boards in the trial' },
  { key: 'trial.writeMessages', description: 'Post to any site message board & broadcast to all sites' }
]
export const GROUP_PERMISSIONS = [
  { key: 'group.read', description: 'Read group participants & data' },
  { key: 'group.write', description: 'Read & change group participant data' }
]
export const SITE_PERMISSIONS = [
  { key: 'site.access', description: 'Be a site member (recommended for any site roles)' },
  { key: 'site.read', description: 'Site-wide admin view access' },
  { key: 'site.write', description: 'Site-wide admin write access' },
  { key: 'site.readParticipants', description: 'List all participants at the site' },
  { key: 'site.readFiles', description: 'Read site files' },
  { key: 'site.readApprovedFiles', description: 'View approved site files only' },
  { key: 'site.readFileVersions', description: 'View site file versions' },
  { key: 'site.writeFiles', description: 'Create new site files and versions' },
  { key: 'site.deleteFiles', description: 'Delete site files and versions' },
  { key: 'site.requestFileApproval', description: 'Request site file approval' },
  { key: 'site.approveFiles', description: 'Approve site files' },
  { key: 'site.readUsers', description: 'View site personnel' },
  { key: 'site.listPacks', description: 'List & view packs' },
  { key: 'site.writePacks', description: 'Create & update packs' },
  { key: 'site.assignPacks', description: 'List and assign available packs to participants' },
  { key: 'site.readMessages', description: 'Read this site\'s message board' },
  { key: 'site.writeMessages', description: 'Post to this site\'s message board' }
]

const utils = {
  uuidv4,
  /**
   * `get_trial_stages` turns recurring stages into multiple list rows and, for recurrence > 0,
   * rewrites `_id` to `mongoId::recurrence` so each row is unique in the UI. Backend calls expect
   * the real `stages` collection id and pass recurrence separately (`stageRecurrence`); this returns
   * that id by stripping any `::…` suffix.
   */
  timelineStageMongoId (stage) {
    const s = String(stage?._id ?? '')
    const i = s.indexOf('::')
    return i === -1 ? s : s.slice(0, i)
  },
  formatDate (date) {
    if (!date) return null
    const formatter = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    return formatter.format(new Date(date))
  },
  formatDateTime (date, { withTimezone, withSeconds } = {}) {
    if (!date) return null
    const formatter = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: withSeconds ? '2-digit' : undefined,
      hour12: false
    })
    const formatted = formatter.format(new Date(date))
    if (withTimezone) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      return `${formatted} (${tz})`
    }
    return formatted
  },
  formatTime (timeStr) {
    if (!timeStr) return null
    const s = String(timeStr).trim()
    const parts = s.split(':')
    const h = parseInt(parts[0], 10) || 0
    const m = parseInt(parts[1], 10) || 0
    const sec = parseInt(parts[2], 10) || 0
    const formatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: parts[2] ? '2-digit' : undefined,
      hour12: false
    })
    return formatter.format(new Date(1970, 0, 1, h, m, sec))
  },
  isSameDay (date1, date2) {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
  },
  toast (style, title, description) {
    toaster.create({
      title,
      description,
      type: style,
      duration: 5000
    })
  },
  hasFeature (team, feature) {
    return team?.enabledFeatures?.indexOf(feature) > -1
  },
  copyToClipboard (text) {
    navigator.clipboard.writeText(text).then(
      () => utils.toast('success', 'Copied to clipboard'),
      err => utils.toast('error', `Error copying: ${err}`)
    )
  },
  hasPermission (user, permission, scope) {
    return user?.permissions && (
      user.permissions.global?.indexOf('root') > -1 ||
      user.permissions.global?.indexOf(permission) > -1 ||
      (user.permissions[scope] && user.permissions[scope].indexOf(permission) > -1)
    )
  },
  canManageTeam (user, team) {
    return utils.hasPermission(user, 'team.manage', team?._id)
  },
  canManageTrial (user, trial) {
    return utils.hasPermission(user, 'trials.manage', trial?.team) || utils.hasPermission(user, 'trial.admin', trial?._id)
  },
  canManageTrialWallet (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.manageWallet', trial?._id)
  },
  canUseTrialAssistant (user, trial) {
    return utils.hasPermission(user, 'trial.useAssistant', trial?._id)
  },
  canReadTrialInsights (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readInsights', trial?._id)
  },
  canWriteTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.write', trial._id)
  },
  canReadTrial (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.read', trial._id)
  },
  canReadParticipantPersonalInformation (user, trial) {
    return utils.hasPermission(user, 'trial.readParticipantPersonalInformation', trial._id)
  },
  canReadParticipantConsent (user, trial) {
    return utils.hasPermission(user, 'trial.readParticipantConsent', trial._id)
  },
  canReadTrialConsents (user, trial) {
    return utils.hasPermission(user, 'trial.readTrialConsents', trial._id)
  },
  canReadTrialUsers (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readUsers', trial._id)
  },
  canReadTrialQueries (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readQueries', trial._id)
  },
  canWriteTrialQueries (user, trial) {
    if (!user || !trial?._id) return false
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeQueries', trial._id)
  },
  canAccessTrial (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.access', trial._id)
  },
  canSupportTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.support', trial._id)
  },
  canWriteTrialFiles (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.writeFiles', trial._id)
  },
  canWriteSiteFiles (user, trial, site) {
    return utils.canWriteTrialFiles(user, trial) || utils.hasPermission(user, 'site.writeFiles', site?._id)
  },
  canReadTrialMessages (user, trial) {
    // Write access implies read access (mirrors the API's can_read_trial_messages).
    return utils.canWriteTrialMessages(user, trial) || utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readMessages', trial?._id)
  },
  canWriteTrialMessages (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeMessages', trial?._id)
  },
  canReadSiteMessages (user, trial, site) {
    if (!user || !trial || !site) return false
    return utils.canReadTrialMessages(user, trial) || utils.canWriteSiteMessages(user, trial, site) || utils.hasPermission(user, 'site.readMessages', site._id)
  },
  canWriteSiteMessages (user, trial, site) {
    if (!user || !trial || !site) return false
    return utils.canWriteTrialMessages(user, trial) || utils.hasPermission(user, 'site.writeMessages', site._id)
  },
  canWriteFile (user, trial, file) {
    return utils.canWriteTrialFiles(user, trial) || utils.hasPermission(user, 'trial.writeFiles', file.trial) || utils.hasPermission(user, 'site.writeFiles', file.site)
  },
  canDeleteFile (user, trial, file) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.deleteFiles', file.trial) || utils.hasPermission(user, 'site.deleteFiles', file.site)
  },
  canApproveFile (user, trial, file) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.approveFiles', file?.trial) || utils.hasPermission(user, 'site.approveFiles', file?.site)
  },
  canReadFileVersions (user, trial, file) {
    if (!user || !trial || !file) return false
    if (utils.canReadTrial(user, trial)) return true
    if (utils.hasPermission(user, 'trial.readFileVersions', trial._id)) return true
    if (file.site && utils.hasPermission(user, 'site.readFileVersions', file.site)) return true
    return false
  },
  canRequestFileApproval (user, trial, file) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.requestFileApproval', file.trial) || utils.hasPermission(user, 'site.requestFileApproval', file.site)
  },
  canReadTrialSites (user, trial) {
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readSites', trial._id)
  },
  canWriteTrialSites (user, trial) {
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeSites', trial._id)
  },
  canWriteGroup (user, group) {
    return utils.hasPermission(user, 'group.write', group?._id)
  },
  canReadGroup (user, group) {
    return utils.canWriteGroup(user, group) || utils.hasPermission(user, 'group.read', group?._id)
  },
  canWriteSite (user, site) {
    return utils.hasPermission(user, 'site.write', site?._id)
  },
  canReadSite (user, site) {
    return utils.canWriteSite(user, site) || utils.hasPermission(user, 'site.read', site?._id)
  },
  canListPacksTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.listPacks', trial?._id)
  },
  canWritePacksTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.writePacks', trial?._id)
  },
  canDeletePacksTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.deletePacks', trial?._id)
  },
  canAssignPacksTrial (user, trial) {
    return utils.canManageTrial(user, trial) || utils.hasPermission(user, 'trial.assignPacks', trial?._id)
  },
  canListPacksSite (user, site) {
    return utils.hasPermission(user, 'site.listPacks', site?._id)
  },
  canWritePacksSite (user, site) {
    return utils.hasPermission(user, 'site.writePacks', site?._id)
  },
  canDeletePacksSite (user, site) {
    return utils.hasPermission(user, 'site.deletePacks', site?._id)
  },
  canAssignPacksSite (user, site) {
    return utils.hasPermission(user, 'site.assignPacks', site?._id)
  },
  canListPacksForParticipant (user, trial, participant) {
    if (!user || !trial || !participant) return false
    if (utils.canListPacksTrial(user, trial)) return true
    for (const siteId of (participant.sites ?? [])) {
      if (utils.canListPacksSite(user, { _id: siteId })) return true
    }
    return false
  },
  canAssignPacksForParticipant (user, trial, participant) {
    if (!user || !trial || !participant) return false
    if (utils.canAssignPacksTrial(user, trial)) return true
    for (const siteId of (participant.sites ?? [])) {
      if (utils.canAssignPacksSite(user, { _id: siteId })) return true
    }
    return false
  },
  canCreateParticipantMeetings (user, trial) {
    if (!user || !trial) return false
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.createParticipantMeetings', trial?._id)
  },
  canReadParticipantMeetings (user, trial) {
    if (!user || !trial) return false
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readParticipantMeetings', trial?._id)
  },
  canJoinParticipantMeetings (user, trial) {
    if (!user || !trial) return false
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.joinParticipantMeetings', trial?._id)
  },
  canDeleteParticipantMeetings (user, trial) {
    if (!user || !trial) return false
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.deleteParticipantMeetings', trial?._id)
  },
  canReadParticipantNotes (user, trial) {
    if (!user || !trial) return false
    return utils.canReadTrial(user, trial) || utils.hasPermission(user, 'trial.readParticipantNotes', trial?._id)
  },
  canWriteParticipantNotes (user, trial) {
    if (!user || !trial) return false
    return utils.canWriteTrial(user, trial) || utils.hasPermission(user, 'trial.writeParticipantNotes', trial?._id)
  },
  canWriteParticipant (user, trial, participant) {
    if (!user || !trial || !participant) return false
    if (utils.canWriteTrial(user, trial)) return true
    for (const group of (participant.groups ?? [])) {
      if (utils.canWriteGroup(user, { _id: group })) return true
    }
    for (const site of (participant.sites ?? [])) {
      if (utils.canWriteSite(user, { _id: site })) return true
    }
    return false
  },
  canReadParticipant (user, trial, participant) {
    if (utils.canWriteParticipant(user, trial, participant)) return true
    if (!user || !trial || !participant) return false
    if (utils.canReadTrial(user, trial)) return true
    for (const group of (participant.groups ?? [])) {
      if (utils.canReadGroup(user, { _id: group })) return true
    }
    for (const site of (participant.sites ?? [])) {
      if (utils.canReadSite(user, { _id: site })) return true
    }
    return false
  },
  randomAlphaNumeric (length) {
    const o = '3456789ABCDEFGJKLMNPRSTUVWXY'
    let s = ''
    while (s.length < length) s += o[Math.floor(Math.random() * o.length)]
    return s
  },
  ensureHttp (s) {
    if (s && s.toLowerCase().indexOf('http') === -1) return `http://${s}`
    return s
  },
  activePath (pattern) {
    const match = window.location.pathname.match(new RegExp(pattern, 'i'))
    return match && match.length > 0
  },
  absoluteUrl (path) {
    return window.location.protocol + '//' + window.location.host + path
  },
  hasEmailSubscription (user, sub) {
    return user && user.subscriptions && user.subscriptions.email && user.subscriptions.email.indexOf(sub) > -1
  },
  teamLogo (team) {
    return (team && team.logoUrl) || logo
  },
  debounce (callback, delay) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => callback(...args), delay || 2000) // eslint-disable-line
    }
  },
  kToLbs (pK) {
    const nearExact = parseFloat(pK) / 0.45359237
    const lbs = Math.floor(nearExact)
    const oz = (nearExact - lbs) * 16
    return {
      pounds: lbs,
      ounces: oz
    }
  },
  lbToK (pounds, ounces) {
    const totalPounds = parseFloat(pounds) + parseFloat(ounces) / 16
    return totalPounds * 0.45359237
  },
  cmToFeet (cm) {
    const totalInches = parseFloat(cm) / 2.54 // 1 inch = 2.54 cm [web:2][web:8]
    const feet = Math.floor(totalInches / 12)
    const inches = +(totalInches % 12).toFixed(2) // round to 2 decimal places
    return { feet, inches }
  },
  feetToCm (feet, inches) {
    return parseInt(feet) * 30.48 + parseFloat(inches) * 2.54
  },
  celsiusToFahrenheit (c) {
    if (isNaN(parseFloat(c))) return
    return (parseFloat(c) * (9 / 5)) + 32
  },
  fahrenheitToCelsius (f) {
    if (isNaN(parseFloat(f))) return
    return (parseFloat(f) - 32) * (5 / 9)
  },
  shuffleArray (a) {
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]]
    }
    return a
  },
  getAutomaticMessageScheduleAsString (message, groups, anchors) {
    const s = message?.schedule || {}
    let scheduleString = ''
    if (s.frequency === 'specific') {
      scheduleString += `On: ${s.dates?.map(d => utils.formatDateTime(d, { withTimezone: true })).join(', ')}`
    } else if (s.frequency === 'daily') scheduleString += `Every day at ${s.hour}:00 (participant local time)`
    else if (s.frequency === 'weekly') scheduleString += `Every ${DAYS[s.day]} at ${s.hour}:00 (participant local time)`
    const messageGroup = groups?.find(g => g._id === s?.group)
    if (messageGroup) {
      scheduleString += ` (${messageGroup.name})`
    }
    // Anchor-relative messages are a separate sendWhen variant (not a schedule
    // frequency); display them as "N days after <anchor>, HH:00 participant local time".
    if (message?.sendWhen === 'anchor') {
      const offset = s.dayOffset ?? 0
      const anchorName = anchors?.find(a => a._id === s.anchor)?.name || 'the anchor'
      const h = Number(s.hour ?? 0)
      const hourLabel = `${h < 10 ? '0' : ''}${h}:00`
      scheduleString = `${Math.abs(offset)} day${Math.abs(offset) === 1 ? '' : 's'} ${offset < 0 ? 'before' : 'after'} ${anchorName}, ${hourLabel} (participant local time)`
      if (messageGroup) {
        scheduleString += ` (${messageGroup.name})`
      }
    }
    return scheduleString
  },
  downloadFile (url, fileName) {
    const element = document.createElement('a')
    element.setAttribute('href', url)
    element.setAttribute('download', fileName)
    element.setAttribute('target', '_blank')
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  },
  uploadFile (file, forType, forId, onComplete, onError) {
    api.uploads.generateFileUploadRequest({
      forType,
      forId,
      name: file.name,
      size: file.size,
      type: file.type
    }, (response) => {
      const xhr = new window.XMLHttpRequest()
      xhr.open('PUT', response.signedRequest)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            onComplete({ expectedUrl: response.expectedUrl, storedName: response.fileName, name: file.name, type: 'file' })
          } else if (onError) {
            onError('Unable to upload file')
          }
        }
      }
      xhr.send(file)
    }, (err) => {
      if (onError) onError(err.message || 'Unable to upload file')
    })
  },
  oauthLoginStateStorageKey (provider) {
    return `trialflare_oauth_login_state_${provider}`
  },
  randomOAuthState () {
    const a = new Uint8Array(32)
    window.crypto.getRandomValues(a)
    return Array.from(a, b => b.toString(16).padStart(2, '0')).join('')
  },
  loginWithGoogle () {
    const url = 'https://accounts.google.com/o/oauth2/v2/auth'
    const redirectUri = `${window.location.protocol}//${window.location.host}/login/oauth/google`
    const scope = 'openid email profile'
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const state = utils.randomOAuthState()
    window.sessionStorage.setItem(utils.oauthLoginStateStorageKey('google'), state)
    const q = new URLSearchParams({
      client_id: clientId,
      scope,
      redirect_uri: redirectUri,
      response_type: 'code',
      state
    })
    window.location = `${url}?${q.toString()}`
  },
  loginWithMicrosoft () {
    const url = 'https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize'
    const redirectUri = `${window.location.protocol}//${window.location.host}/login/oauth/microsoft`
    const scope = 'openid email offline_access User.Read'
    const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID
    const state = utils.randomOAuthState()
    window.sessionStorage.setItem(utils.oauthLoginStateStorageKey('microsoft'), state)
    const q = new URLSearchParams({
      client_id: clientId,
      scope,
      redirect_uri: redirectUri,
      response_type: 'code',
      state
    })
    window.location = `${url}?${q.toString()}`
  },
  connectToGoogle (scope, returnTo, serviceName) {
    const url = 'https://accounts.google.com/o/oauth2/v2/auth'
    const redirectUri = `${window.location.protocol}//${window.location.host}/connect/google`
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const callback = returnTo || window.location.pathname
    const state = JSON.stringify({ service: serviceName || 'google', scope, callback })
    window.location = `${url}?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&response_type=code&state=${btoa(state)}&prompt=consent&access_type=offline`
  },
  connectToMicrosoft (scope, returnTo, serviceName) {
    const url = 'https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize'
    const redirectUri = `${window.location.protocol}//${window.location.host}/connect/microsoft`
    const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID
    const callback = returnTo || window.location.pathname
    const state = JSON.stringify({ service: serviceName || 'microsoft', scope, callback })
    window.location = `${url}?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&response_type=code&state=${btoa(state)}&prompt=consent&access_type=offline`
  },
  connectToDropbox (scope, returnTo, serviceName) {
    const url = 'https://www.dropbox.com/oauth2/authorize'
    const redirectUri = `${window.location.protocol}//${window.location.host}/connect/dropbox`
    const clientId = import.meta.env.VITE_DROPBOX_CLIENT_ID
    const callback = returnTo || window.location.pathname
    const state = JSON.stringify({ service: serviceName || 'dropbox', scope, callback })
    window.location = `${url}?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&response_type=code&state=${btoa(state)}&token_access_type=offline`
  }

}

/**
 * Evaluate a single condition against a response value
 * @param {Object} condition - The condition object with operator and value
 * @param {*} conditionResp - The response value to evaluate against
 * @returns {boolean} - True if condition is met
 */
export function evaluateCondition (condition, conditionResp) {
  // Handle BMI type: extract the bmi value from the object
  if (conditionResp && typeof conditionResp === 'object' && !Array.isArray(conditionResp) && 'bmi' in conditionResp) {
    conditionResp = conditionResp.bmi
  }

  const exists =
    conditionResp !== null && conditionResp !== undefined && conditionResp !== '' &&
    (Array.isArray(conditionResp) ? (conditionResp?.length > 0) : true)

  const isEqual =
    conditionResp?.toString()?.toLowerCase()?.trim() === condition.value?.toString()?.toLowerCase()?.trim() ||
    (Array.isArray(condition?.value) && condition?.value.indexOf(conditionResp) > -1) ||
    (Array.isArray(conditionResp) && conditionResp.indexOf(condition?.value) > -1) ||
    (Array.isArray(condition?.value) && Array.isArray(conditionResp) && condition.value.filter(x => conditionResp?.indexOf(x) !== -1)?.length > 0)

  return (
    (condition.operator === 'exists' && exists) ||
    (condition.operator === 'equal' && isEqual) ||
    (condition.operator === 'notequal' && !isEqual) ||
    (condition.operator === 'more' && conditionResp > condition.value) ||
    (condition.operator === 'less' && conditionResp < condition.value)
  )
}

/**
 * Build a component visibility map that handles chained conditionals
 * Processes components in order and accounts for dependencies (e.g., C depends on B depends on A)
 * @param {Array} components - Array of component objects from the stage
 * @param {Object} response - Current response data (object mapping dataType to {value, ...})
 * @param {Object} existingResponse - Optional existing response data for fallback
 * @returns {Object} - Map of dataType_id to boolean indicating visibility
 */
export function buildComponentVisibilityMap (components, response, existingResponse = {}) {
  const visibilityMap = {}
  const mergedResponse = { ...existingResponse, ...response }

  // Process components in order (as they appear in the stage)
  components?.forEach(c => {
    if (!c.dataType) return

    const dataTypeId = c.dataType

    // If no condition, component is always visible
    if (!c.condition) {
      visibilityMap[dataTypeId] = true
      return
    }

    // Check if the dependency component is visible first (handles chained conditionals)
    const dependencyDataTypeId = c.condition.dataType
    if (dependencyDataTypeId in visibilityMap) {
      const dependencyVisible = visibilityMap[dependencyDataTypeId]
      if (!dependencyVisible) {
        // The component this depends on is not visible, so this component cannot be visible
        visibilityMap[dataTypeId] = false
        return
      }
    }

    // Get the condition response value
    const conditionResp = mergedResponse[dependencyDataTypeId]?.value

    // Evaluate the condition
    const conditionMet = evaluateCondition(c.condition, conditionResp)
    visibilityMap[dataTypeId] = conditionMet
  })

  return visibilityMap
}

/**
 * Check if a component's condition is met using a pre-built visibility map
 * @param {Object} component - The component to check
 * @param {Object} visibilityMap - Pre-built visibility map from buildComponentVisibilityMap
 * @returns {boolean} - True if component should be visible
 */
export function componentConditionIsMet (component, visibilityMap) {
  if (!component.condition) return true
  if (!component.dataType) return true

  // Use the pre-computed visibility map which handles chained dependencies
  return visibilityMap[component.dataType] ?? true
}

/**
 * Format KYCAID applicant addresses for display (Consents table, participant Consent tab).
 * @param {Array} addresses - applicant.addresses from KYCAID payload
 * @returns {string} - Formatted address(es), newline-separated if multiple, or '—'
 */
export function formatKycaidAddresses (addresses) {
  if (!addresses?.length) return '—'
  return addresses.map(a => {
    const part = a.full_address || [a.street_name, a.building_number, a.city, a.state_or_province || a.region, a.postal_code, a.country].filter(Boolean).join(', ')
    return part || '—'
  }).join('\n')
}

/**
 * True if the chosen file looks like MP4 (MIME video/mp4 or .mp4 name).
 * Browsers may report an empty or generic MIME for some files; extension is a fallback.
 */
export function isLikelyMp4Upload ({ mimeType, name } = {}) {
  const mt = (mimeType || '').toLowerCase().trim()
  if (mt === 'video/mp4') return true
  const base = (name || '').toLowerCase().split(/[/\\]/).pop() || ''
  return base.endsWith('.mp4')
}

export default utils
